import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { addLiveTicks } from "@/state/slices/analysisSlice";
import type { Tick } from "./useTickStream";

/**
 * Single live-stream subscription mounted at app root. Watches the
 * analysis slice's `liveTabId` (single-live rule — at most one tab
 * can be Live at a time) and the owning tab's symbol. Opens an
 * EventSource for that symbol while Live is active, routing each
 * batch through `addLiveTicks` which the slice fans into the
 * liveTabId-owned bins.
 *
 * The EventSource closes the moment `liveTabId` clears, the live
 * owner switches symbols, or another tab takes over the live session.
 */
export function useLiveAnalysis(): void {
  const liveTabId = useAppSelector((s) => s.analysis.liveTabId);
  const liveSymbol = useAppSelector((s) =>
    s.analysis.liveTabId ? s.ui.byTab[s.analysis.liveTabId]?.symbol ?? null : null,
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!liveTabId || !liveSymbol) return;
    const url = `/api/stream/ticks?symbol=${encodeURIComponent(liveSymbol)}`;
    const es = new EventSource(url);
    const onTick = (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data);
        const ticks: Tick[] = data.ticks ?? [];
        if (ticks.length > 0) dispatch(addLiveTicks(ticks));
      } catch {
        // ignore malformed
      }
    };
    // Intentionally NOT listening to the SSE `snapshot` event — that
    // would inject the server's last ~500 buffered ticks (from before
    // the user clicked Live Analyze) and make it look like the
    // analysis was already running. Live Analyze must accumulate
    // *only* from ticks delivered after the click.
    es.addEventListener("tick", onTick as EventListener);
    return () => {
      es.removeEventListener("tick", onTick as EventListener);
      es.close();
    };
  }, [liveTabId, liveSymbol, dispatch]);
}
